/**
 * MyReportsList — shared component that fetches + renders the
 * cross-project report_jobs feed.
 *
 * Used by:
 *   • <ReportsDrawer />        — top-nav bell drawer (`side="right"` sheet)
 *   • <MyReportsPage />        — full-page `/reports` route
 *
 * Auto-polls every 4 s while any job is in ``pending`` or ``running``
 * state; stops polling once every job is terminal.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  FileText, Download, RefreshCcw, Loader2, Clock, CheckCircle2, XCircle,
} from "lucide-react";


const STATUS_PILL = {
  completed: { icon: CheckCircle2, cls: "text-green-700 bg-green-50 border-green-200",       label: "Completed" },
  running:   { icon: Loader2,      cls: "text-blue-700 bg-blue-50 border-blue-200 animate-pulse", label: "Running" },
  pending:   { icon: Clock,        cls: "text-amber-700 bg-amber-50 border-amber-200",           label: "Pending" },
  failed:    { icon: XCircle,      cls: "text-red-700 bg-red-50 border-red-200",                 label: "Failed" },
};


function StatusPill({ status }) {
  const s = STATUS_PILL[status] || STATUS_PILL.pending;
  const Icon = s.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest border px-2 py-0.5 ${s.cls}`}
      data-testid={`report-job-status-${status}`}
    >
      <Icon className={`h-3 w-3 ${status === "running" ? "animate-spin" : ""}`} strokeWidth={2} />
      {s.label}
    </span>
  );
}


function fmtSize(bytes) {
  const n = Number(bytes) || 0;
  if (n >= 1024 * 1024) return `${(n / 1024 / 1024).toFixed(2)} MB`;
  if (n >= 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${n} B`;
}


function fmtWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const now = new Date();
  const diffMs = now - d;
  const s = Math.floor(diffMs / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return d.toLocaleString(undefined, {
    year: "2-digit", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}


export default function MyReportsList({ compact = false, onNavigate }) {
  const [jobs, setJobs]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/report-jobs", { params: { limit: compact ? 25 : 100 } });
      setJobs(Array.isArray(data) ? data : []);
    } catch (e) {
      // Non-blocking — surface a toast on manual refresh but not on autopoll.
    } finally { setLoading(false); }
  }, [compact]);

  // Initial fetch + auto-poll every 4 s while any job is pending/running.
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const hasLive = jobs.some((j) => j.status === "pending" || j.status === "running");
    if (!hasLive) {
      if (timer.current) { clearInterval(timer.current); timer.current = null; }
      return;
    }
    if (timer.current) return;                    // already polling
    timer.current = setInterval(load, 4000);
    return () => { if (timer.current) { clearInterval(timer.current); timer.current = null; } };
  }, [jobs, load]);

  const download = useCallback(async (job) => {
    if (!job?.download_url) return;
    setBusyId(job.job_id);
    try {
      const path = job.download_url.replace(/^\/api/, "");
      const resp = await api.get(path, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: job.content_type || "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = job.filename || "report.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(`Download failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusyId(null); }
  }, []);

  const grouped = useMemo(() => {
    const g = { live: [], done: [] };
    for (const j of jobs) {
      if (j.status === "pending" || j.status === "running") g.live.push(j);
      else g.done.push(j);
    }
    return g;
  }, [jobs]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground text-sm font-mono">
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        Loading reports…
      </div>
    );
  }

  if (jobs.length === 0) {
    return (
      <div className="border border-dashed border-border p-8 text-center space-y-2"
           data-testid="my-reports-empty">
        <FileText className="h-8 w-8 mx-auto text-muted-foreground" strokeWidth={1.5} />
        <p className="text-sm font-medium">No reports yet</p>
        <p className="text-xs text-muted-foreground max-w-sm mx-auto">
          Generate a PDF, ZIP bundle or comprehensive report from any
          project and it will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="my-reports-list">
      <div className="flex items-center justify-between">
        <div className="smin-label">
          {jobs.length} report{jobs.length === 1 ? "" : "s"}
          {grouped.live.length > 0 && (
            <span className="ml-2 text-amber-600 font-mono text-[11px]">
              · {grouped.live.length} in progress
            </span>
          )}
        </div>
        <Button
          onClick={load}
          variant="outline"
          size="sm"
          className="rounded-none h-7 px-2 text-xs"
          data-testid="my-reports-refresh"
        >
          <RefreshCcw className="h-3 w-3 mr-1" /> Refresh
        </Button>
      </div>

      <div className="space-y-2">
        {[...grouped.live, ...grouped.done].map((job) => (
          <div
            key={job.job_id}
            className="border border-border bg-card p-3 hover:bg-accent/30 transition-colors"
            data-testid="my-reports-row"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <StatusPill status={job.status} />
                  <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                    {job.kind}
                  </span>
                  <span className="text-[10px] font-mono text-muted-foreground">
                    · {fmtWhen(job.created_at)}
                  </span>
                </div>
                <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                  <Link
                    to={`/projects/${job.project_id}`}
                    onClick={onNavigate}
                    className="text-xs font-mono text-primary hover:underline truncate"
                    title="Open project"
                  >
                    {job.project_name || "(project)"}
                  </Link>
                </div>
                <p className="mt-1 text-[13px] font-medium truncate" title={job.filename}>
                  {job.filename}
                </p>
                {job.status === "completed" && job.size_bytes > 0 && (
                  <p className="text-[10px] font-mono text-muted-foreground mt-0.5">
                    {fmtSize(job.size_bytes)}
                  </p>
                )}
                {job.status === "failed" && job.error && (
                  <p className="text-[11px] text-red-700 mt-1 line-clamp-2">
                    {job.error}
                  </p>
                )}
              </div>
              <div className="shrink-0">
                {job.status === "completed" ? (
                  <Button
                    onClick={() => download(job)}
                    disabled={busyId === job.job_id}
                    size="sm"
                    className="rounded-none h-8"
                    data-testid={`my-reports-download-${job.job_id}`}
                  >
                    {busyId === job.job_id
                      ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      : <Download className="h-3 w-3 mr-1" />}
                    Download
                  </Button>
                ) : (
                  <span className="text-[10px] font-mono text-muted-foreground">
                    {job.status === "failed" ? "—" : "in flight"}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
